import { ecorEnv } from '@/../environments/environment';
import { AppService } from '@/app.service';
import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import * as PortalUserActions from '@/common/components/ecorr-store/portal-user/portal-user.action';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { EventService } from '@/surveys/services/event.service';
import { AfterViewChecked, Component, OnDestroy, OnInit, inject, signal, computed, DestroyRef } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SurveyCard } from './survey-card';
import { SurveysService } from './surveys.service';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GridViewComponent } from '@/surveys/components/survey-views/grid-view/grid-view.component';
import { ListViewComponent } from '@/surveys/components/survey-views/list-view/list-view.component';
import { CalendarViewComponent } from '@/surveys/components/survey-views/calendar-view/calendar-view.component';
import { LinearLoadingComponent } from '@/common/components/linear-loading/linear-loading.component';
import { AddSurveyDialogService } from '@/surveys/services/add-survey-dialog.service';
import { AddSurveyDialogHttpService } from '@/surveys/services/add-survey-dialog-http.service';
import { EnableCookiesDialogService } from '@/surveys/services/enable-cookies-dialog.service';
import { NotificationOptDialogService } from '@/surveys/services/notification-opt-dialog.service';
import { NotificationOptDialogHttpService } from '@/surveys/services/notification-opt-dialog-http.service';
import { WelcomeSurveyDialogService } from '@/surveys/services/welcome-survey-dialog.service';
import { FilingStatusDialogService } from '@/surveys/services/filing-status-dialog.service';
import { RequestExtensionDialogService } from '@/surveys/services/request-extension-dialog.service';
import { RequestExtensionDialogHttpService } from '@/surveys/services/request-extension-dialog-http.service';
import { ShareSurveyAccessDialogService } from '@/surveys/services/share-survey-access-dialog.service';
import { CalendarDialogService } from '@/surveys/services/calendar-dialog.service';
import { SurveyViewsBaseService } from '@/surveys/services/survey-views-base.service';
import { SurveyViewsInstanceService } from '@/surveys/services/survey-views-instance.service';
import { AppSettingsService } from '@/common/services/app-settings.service';
import { FileUtilService } from '@/common/utils/file-util.service';
import { EcorrUtilsService } from '@/common/utils/ecorr-utils.service';
import { SessionStorageService } from 'ngx-webstorage';
import { CanisterService } from '@/surveys/services/canister.service';
import { SurveySelectionDialogService } from '@/surveys/services/survey-selection-dialog.service';
import { ExportSurveyDialogService } from '@/surveys/services/export-survey-dialog.service';
import { DatePipe } from '@angular/common';
import { CustomTabsComponent } from '@/common/components/custom-tabs/custom-tabs.component';

@Component({
    selector: 'app-surveys',
    templateUrl: './surveys.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatTabsModule,
        MatIconModule,
        MatListModule,
        MatButtonModule,
        MatCardModule,
        MatTooltipModule,
        GridViewComponent,
        ListViewComponent,
        CalendarViewComponent,
        LinearLoadingComponent,
        CustomTabsComponent
    ],
    providers: [
        AddSurveyDialogService,
        AddSurveyDialogHttpService,
        EnableCookiesDialogService,
        NotificationOptDialogService,
        NotificationOptDialogHttpService,
        WelcomeSurveyDialogService,
        FilingStatusDialogService,
        RequestExtensionDialogService,
        ShareSurveyAccessDialogService,
        CalendarDialogService,
        SurveyViewsBaseService,
        SurveyViewsInstanceService,
        FileUtilService,
        EcorrUtilsService,
        SessionStorageService,
        CanisterService,
        SurveySelectionDialogService,
        ExportSurveyDialogService,
        DatePipe,
        RequestExtensionDialogHttpService
    ]
})
export class SurveysComponent implements OnInit, OnDestroy, AfterViewChecked {
    // Services using inject()
    selectedTabIndex = 0;
    private store = inject(Store<PortalUserState>);
    private surveysService = inject(SurveysService);
    private router = inject(Router);
    private appService = inject(AppService);
    private paradataEventService = inject(ParadataEventService);
    private eventService = inject(EventService);
    private ecorrUtilsService = inject(EcorrUtilsService);
    private destroyRef = inject(DestroyRef);

    // Signals for reactive state
    surveyCards = signal<SurveyCard[]>([]);
    loading = signal(false);
    active = signal(true);
    cardId = signal<string | null>(null);
    justAdded = signal<number | undefined>(undefined);
    overdueMessage = signal('');
    view = signal('grid');
    activeSurveys = signal<SurveyCard[] | null>(null);
    inactiveSurveys = signal<SurveyCard[] | null>(null);
    tmpSurveys = signal<SurveyCard[]>([]);
    overdueSurveys = signal<string[]>([]);
    user = signal<PortalUser | null>(null);

    // Computed signals
    currentSurveyCards = computed(() => this.surveyCards());
    isLoading = computed(() => this.loading());
    isActive = computed(() => this.active());
    currentCardId = computed(() => this.cardId());
    currentJustAdded = computed(() => this.justAdded());
    currentOverdueMessage = computed(() => this.overdueMessage());
    currentView = computed(() => this.view());
    currentActiveSurveys = computed(() => this.activeSurveys());
    currentInactiveSurveys = computed(() => this.inactiveSurveys());
    currentTmpSurveys = computed(() => this.tmpSurveys());
    currentOverdueSurveys = computed(() => this.overdueSurveys());
    currentUser = computed(() => this.user());

    constructor() {
        this.user$ = this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                (user: PortalUser) => {
                    this.user.set(user);
                }
            );

        this.sub$ = this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((e: any) => {
                if (e instanceof NavigationEnd) {
                    const currentUser = this.user();
                    if (!currentUser || !currentUser.userAccountId || currentUser.userAccountId === 0) {
                        this.router.navigate(['/portal']);
                    } else {
                        this.getSurveys();
                    }
                }
            });

        this.event$ = this.eventService.eventListner()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                event => {
                    if (event.orgId) {
                        this.view.set('grid');
                        this.cardId.set('card-' + event.orgId);
                    } else if (event.surveyInstanceId) {
                        this.justAdded.set(event.surveyInstanceId);
                    } else if (event.surveyAdded) {
                        this.getSurveys(this.justAdded());
                        this.justAdded.set(undefined);
                    }
                }
            );
    }

    channel$: Subscription;
    event$: Subscription;
    user$: Subscription;
    sub$: Subscription;

    vm: object = this;

    ngAfterViewChecked() {
        if (this.view() === 'grid') {
            const currentCardId = this.cardId();
            if (currentCardId) {
                const elements = document.getElementById(currentCardId);
                if (elements) {
                    elements.scrollIntoView();
                }
                this.cardId.set(null);
            }
        }
    }

    showHelp() {
        window.open(ecorEnv.faqPageLink, '_target');
    }

    onTabClick(index: number) {
        this.selectedTabIndex = index;
        const isActiveTab = index === 0;
        this.active.set(isActiveTab);
        this.tabs[index]?.fn?.();
        this.eventService.emitEvent({ tabChanged: true });

        if (isActiveTab) {
            this.paradataEventService.add(ParadataEventEnum.ActiveTabClick);
        } else {
            this.paradataEventService.add(ParadataEventEnum.SurveyDashboardHistoryTab);

            if (this.view() === 'calendar') {
                this.view.set('grid');
            }
        }
    }

    switchView(source: string, suppressEvent?: boolean) {
        this.view.set(source);

        if ( suppressEvent !== true ) {
            switch (this.view()) {
                case 'grid':
                    this.paradataEventService.add(ParadataEventEnum.GridView);
                    break;
                case 'list':
                    this.paradataEventService.add(ParadataEventEnum.ListView);
                    break;
                case 'calendar':
                    // this.paradataEventService.add(ParadataEventEnum.CalendarView);
                    break;
                default:
                    break;
            }
        }
    }

    tabs: any = [{
        label: 'Report Now',
        icon: 'fa-cog',
        fn: this.activeTabClick
    }, {
        label: 'History',
        icon: 'fa-check-circle-o',
        fn: this.historyTabClick
    }];

    activeTabClick() {
        if (this.active && typeof this.active.set === 'function') {
            this.active.set(true);
        }
    }

    historyTabClick() {
        if (this.active && typeof this.active.set === 'function') {
            this.active.set(false);
        }
    }

    ngOnInit() {
    }

    isIE() {
        return this.ecorrUtilsService.isIE();
    }

    getSurveys(surveyInstanceId?: number) {
        //   // Grab canister element so that it can be persisted after repull
        // const canisterContainer = document.getElementById( 'ecorr-canister-container' );
        // let canister;
        // if ( canisterContainer ) {
        //     canister = canisterContainer.innerHTML;
        // }

        if (this.loading()) {
            return;
        } else {
            this.loading.set(true);
        }

        this.surveyCards.set([]);
        this.overdueSurveys.set([]);
        this.inactiveSurveys.set(null);
        this.activeSurveys.set(null);
        
        const currentUser = this.user();
        if (!currentUser || !currentUser.userAccountId) {
            this.loading.set(false);
            return;
        }
        
        this.channel$ = this.surveysService.getSurveys(currentUser.userAccountId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                (data: SurveyCard[]) => {
                    this.surveyCards.set(data);
                const processedSurveys = [...data];
                const overdueSurveys: string[] = [];
                const inactiveSurveys: SurveyCard[] = [];
                const activeSurveys: SurveyCard[] = [];
                
                processedSurveys.forEach((survey) => {
                    // Reformat surveys to allow easier handling of survey data
                    survey.startDate = new Date(survey.surveyStartDate);
                    survey.endDate = new Date(survey.surveyEndDate);
                    survey.originalDueDate = new Date(survey.originalDueDate);
                    survey.currentDueDate = new Date(survey.mostRecentSurveyExtendedDate);
                    survey.extended = survey.originalDueDate.getTime() !== survey.currentDueDate.getTime();
                    if (typeof survey.accountManager === 'undefined') {
                        survey.accountManager = null;
                    }
                    survey.abbrFull = survey.abbr;
                    survey.abbr = this.formatAbbr(survey);

                    // survey.trustedInformation = this.domSanitizer.bypassSecurityTrustHtml(survey.surveyInformation);

                    if (survey.organizationNameLine2) {
                        survey.organizationName = survey.organizationNameLine1 + ', ' + survey.organizationNameLine2;
                    } else if (survey.organizationNameLine1) {
                        survey.organizationName = survey.organizationNameLine1;
                    }

                    if (survey.completedDate) {
                        survey.formStatus = 3;
                        survey.formStatusText = 'Completed';
                    } else {
                        const today = new Date();
                        today.setHours(0,0,0,0);
                        const dueDate = survey.currentDueDate;
                        dueDate.setHours(0,0,0,0);
                        if (today > dueDate && survey.statusIndicator !== '4') {
                            survey.formStatus = 1;
                            survey.formStatusText = 'Incomplete - Overdue';

                            overdueSurveys.push(survey.displayName);
                        } else {
                            survey.formStatus = 2;
                            survey.formStatusText = 'Incomplete';
                        }
                    }

                    //open options dropdown after auth code burn
                    if (surveyInstanceId && survey.surveyInstanceId === surveyInstanceId) {
                        survey.justAdded = true;
                    }

                    if (survey.statusIndicator === '4') {
                        inactiveSurveys.push(survey);
                    } else {
                        activeSurveys.push(survey);
                    }
                });

                activeSurveys.sort(this.sortSurveysDefault);
                inactiveSurveys.sort(this.sortSurveysDefault);

                // Set the properties with processed data
                this.activeSurveys.set(activeSurveys);
                this.inactiveSurveys.set(inactiveSurveys);
                this.overdueSurveys.set(overdueSurveys);

                let showSendMessage = true;
                for (let i = 0; i < activeSurveys.length; i++) {
                    if (activeSurveys[i].statusIndicator === '2' && activeSurveys[i].reportingEnabled !== 'N') {
                        showSendMessage = false;
                        break;
                    }
                }

                if (overdueSurveys.length === 0) {
                    this.overdueMessage.set('');
                } else if (overdueSurveys.length === 1) {
                    this.overdueMessage.set('You have an overdue survey. Please complete ' + overdueSurveys[0] + '.');
                } else if (overdueSurveys.length > 1) {
                    let message = 'You have overdue surveys. Please complete ';
                    const lastSurvey = overdueSurveys.pop();
                    message += overdueSurveys.join(', ') + ', and ' + lastSurvey + '.';
                    this.overdueMessage.set(message);
                }
                this.loading.set(false);

                this.eventService.emitEvent( {
                    canisterTriggered: {
                        surveys: this.activeSurveys(),
                        trigger: 'PageLoad'
                    }
                });

                if (currentUser) {
                    this.store.dispatch(PortalUserActions.setUser({
                        payload: {
                            portalUser: {
                                ...currentUser,
                                showSendMessage: showSendMessage,
                                activeSurveyCards: this.activeSurveys(),
                                inActiveSurveyCards: this.inactiveSurveys()
                            }
                        }
                    }));
                }
            },
            (error: any) => {
                console.error('surveys', 'getSurveys', 'error', error);
                this.surveyCards.set([]);
                this.loading.set(false);
            },
            () => {
                this.appService.loading = false;
            }
        );
    }

    /**
     * Sorting algorithm for survey card sorting
     * */
    sortSurveysDefault(survA, survB) {
        // Sort first by status
        if (survA.formStatus !== survB.formStatus) {
            return survA.formStatus - survB.formStatus;
        } else {
            // Then due/extended date within each status
            const aExt = survA.originalDueDate.getTime() !== survA.currentDueDate.getTime();
            const bExt = survB.originalDueDate.getTime() !== survB.currentDueDate.getTime();

            // If no/two extension date(s), sort purely by current due date
            // If one extension date, put extended survey last
            if ((aExt && bExt) || (!aExt && !bExt)) {
                return survA.currentDueDate - survB.currentDueDate;
            } else {
                return !aExt && bExt ? -1 : 1;
            }
        }
    }

    /**
     * Only pay attention to first word and ignore any trailing numbers in it
     * TODO: Figure out a better way to keep FAQ naming generic while still accepting things like island areas gracefully so this won't expand even more 
     */
    formatAbbr(survey: SurveyCard) {
        if (survey.abbr) {
            if (survey.infoUrl && survey.infoUrl.toLowerCase().includes('island-areas.html')) {
                return survey.abbr.split(' ')[0].replace(/\d+$/, '') + ' - ISLAND';
            } else {
                return survey.abbr.split(' ')[0].replace(/\d+$/, '');
            }
        }
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
        if (this.channel$) {
            this.channel$.unsubscribe();
        }
        if (this.sub$) {
            this.sub$.unsubscribe();
        }
        if (this.user$) {
            this.user$.unsubscribe();
        }
        if (this.event$) {
            this.event$.unsubscribe();
        }
    }

}
